import { aiserverbaselink } from '../../../Env_Variables';
import { aiInstituteHeaders } from './aiInstituteHeaders';
import { importUserCatalogRows } from './aiTools';

export const IMPORT_FILE_ACCEPT = '.xlsx,.xls,.csv,.pdf,.docx,.doc';
export const IMPORT_MAX_BYTES = 10 * 1024 * 1024;
export const IMPORT_KINDS = [
    { id: 'product', en: 'Product', ar: 'منتج' },
    { id: 'collection', en: 'Collection', ar: 'كولكشن' },
    { id: 'parentcollection', en: 'Parent collection', ar: 'أب كولكشن' },
    { id: 'category', en: 'Category', ar: 'فئة' },
];

const ALLOWED_EXT = new Set(['xlsx', 'xls', 'csv', 'pdf', 'docx', 'doc']);

export function importKindLabel(kind, isRtl) {
    const found = IMPORT_KINDS.find((k) => k.id === kind);
    if (!found) return kind || '';
    return isRtl ? found.ar : found.en;
}

export function fileExtension(name) {
    const m = String(name || '')
        .toLowerCase()
        .match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
}

export function validateCatalogImportFile(file) {
    if (!file) return 'Attach one Excel, CSV, PDF, or Word file.';
    if (file.size > IMPORT_MAX_BYTES) return 'That file is larger than 10MB.';
    const ext = fileExtension(file.name);
    if (!ALLOWED_EXT.has(ext)) return 'Use Excel, CSV, PDF, or Word (.docx) — one file at a time.';
    if (ext === 'doc') return 'Old .doc files are not supported. Save as .docx, Excel, CSV, or PDF.';
    return '';
}

function publicImportResult(imported) {
    if (!imported || typeof imported !== 'object') return { ok: false };
    return {
        ok: Boolean(imported.ok),
        uploaded: Boolean(imported.uploaded ?? imported.imported),
        row_count: imported.row_count,
        filename: imported.filename,
        grouptype: imported.grouptype,
        collectionid: imported.collectionid || null,
        collection_title: imported.collection_title || null,
        open_path: imported.open_path || imported.navigate?.path || null,
        preview: imported.preview || [],
        error: imported.error || imported.reason || null,
        note: imported.note || null,
    };
}

export async function convertAndImportCatalogFile({
    file,
    kind,
    note,
    signal,
    onStatus,
    isRtl,
} = {}) {
    const fileError = validateCatalogImportFile(file);
    if (fileError) return { ok: false, error: fileError };
    if (!IMPORT_KINDS.some((k) => k.id === kind)) {
        return {
            ok: false,
            error: isRtl
                ? 'اختَر نوع الملف: منتج أو كولكشن أو أب أو فئة.'
                : 'Choose whether this file is products, collections, parent collections, or categories.',
        };
    }

    onStatus?.(isRtl ? 'بيجهّز الكتالوج…' : 'Preparing the catalog…');
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    if (note) form.append('note', note);

    const response = await fetch(`${aiserverbaselink}/instowner/ai/import/convert`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...aiInstituteHeaders(),
        },
        body: form,
        signal,
    });
    const converted = await response.json().catch(() => ({}));
    if (!response.ok || converted.ok === false) {
        return {
            ok: false,
            error:
                converted.error ||
                (isRtl ? 'تعذر تحويل الملف.' : 'Tabexawy could not convert that file.'),
            convert: converted,
        };
    }

    onStatus?.(isRtl ? 'بيرفع الملف بالصيغة الصح…' : 'Uploading the Tabex import sheet…');
    const imported = await importUserCatalogRows({
        kind,
        rows: converted.rows || [],
        collection_query: converted.collection_query || '',
        filename: converted.filename
            ? String(converted.filename).replace(/\.[^.]+$/, '') + '-tabex.xlsx'
            : undefined,
        signal,
    });

    const summary = publicImportResult(imported);
    return {
        ok: Boolean(summary.ok),
        kind,
        filename: file.name,
        convert: {
            row_count: converted.row_count,
            mapping: converted.mapping || null,
            warnings: converted.warnings || [],
            edits: converted.edits || [],
            collection_query: converted.collection_query || '',
            skipped_model: Boolean(converted.skipped_model),
            preview: converted.preview || [],
            usage: converted.usage || null,
        },
        import: summary,
        navigate: imported?.navigate || null,
        error: summary.ok ? null : summary.error || (isRtl ? 'الرفع فشل.' : 'Upload failed.'),
    };
}
